import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { fetchSupportRequests, type SupportRequest } from '@features/support/api';
import { WidgetCard } from './WidgetCard';
import { widgetTitle, type WidgetProps } from './registry';

const GROUPS: ReadonlyArray<{
  label: string;
  color: string;
  test: (status: SupportRequest['status']) => boolean;
}> = [
  { label: 'Open', color: 'var(--warning)', test: (s) => s === 'open' },
  {
    label: 'In progress',
    color: 'var(--info)',
    test: (s) => s === 'in_progress' || s === 'awaiting_member',
  },
  { label: 'Resolved', color: 'var(--success)', test: (s) => s === 'resolved' || s === 'closed' },
];

/** Home widget: helpdesk stats at a glance, tap-through to the support module. */
export function SupportWidget({ config }: WidgetProps): JSX.Element {
  const navigate = useNavigate();
  const query = useQuery({ queryKey: ['support-requests'], queryFn: fetchSupportRequests });
  const requests = query.data ?? [];

  return (
    <WidgetCard
      title={widgetTitle(config)}
      actionLabel="View all"
      onAction={() => navigate('/app/support/requests')}
      onClick={() => navigate('/app/support/requests')}
    >
      {query.isLoading ? (
        <Skeleton className="h-16" />
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {GROUPS.map((g) => {
            const value = requests.filter((r) => g.test(r.status)).length;
            return (
              <button
                key={g.label}
                type="button"
                onClick={() => navigate('/app/support/requests')}
                style={{ backgroundColor: `color-mix(in oklab, ${g.color} 12%, var(--surface-card))` }}
                className="flex flex-col items-start rounded-xl px-3 py-2.5 text-left active:scale-[0.99]"
              >
                <span className="font-display text-[1.625rem] leading-none" style={{ color: g.color }}>
                  {value}
                </span>
                <span className="mt-1.5 text-[0.625rem] font-semibold uppercase leading-tight text-ink-secondary">
                  {g.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </WidgetCard>
  );
}
